import { Cancel01Icon } from '@hugeicons/core-free-icons';
import { useGetEvent } from '@sunrise-ui/api-client';
import {
  Colors,
  IconButton,
  isNative,
  Modal,
  ProgressBar,
  TextBold,
  TextReg,
  useCheckoutModalStore,
  useDynamicStyles,
  type WithResponsive,
} from '@sunrise-ui/primitives';
import React, { useState } from 'react';
import { StyleSheet, View } from 'react-native';
import type { WithSafeAreaInsetsProps } from 'react-native-safe-area-context';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useCheckoutFormStore } from '../stores/useCheckoutFormStore';
import { AttendeeDetailsStep } from './AttendeeDetailsStep';
import { PaymentStep } from './PaymentStep';
import { SummaryStep } from './SummaryStep';
import { TicketSelectionStep } from './TicketSelectionStep';

export function CheckoutModal() {
  const insets = useSafeAreaInsets();
  const styles = useDynamicStyles(createStyles, { insets });
  const { isOpen, eventId, closeCheckoutModal, currentStep } =
    useCheckoutModalStore();
  const { resetForm } = useCheckoutFormStore();

  const { data: event, isLoading: isLoadingEvent } = useGetEvent(eventId);
  const [isTwoSteps, setIsTwoSteps] = useState(isNative);
  const isSummaryStep = currentStep === 'summary';

  const handleDismiss = () => {
    // Don't allow dismissing during summary step (polling/success/error)
    if (isSummaryStep) {
      return;
    }
    closeCheckoutModal();
    resetForm();
  };

  return (
    <Modal
      visible={isOpen}
      hide={handleDismiss}
      modalStyle={styles.modal}
      contentStyle={styles.modalContent}
      keyboardAvoiding={false}
      header={
        <View style={styles.headerContainer}>
          <View style={styles.header}>
            {isLoadingEvent ? (
              <View style={styles.titleSkeleton} />
            ) : (
              <TextBold style={styles.title}>{event?.name}</TextBold>
            )}
            {isLoadingEvent ? (
              <View style={styles.subtitleSkeleton} />
            ) : event?.start_date ? (
              <TextReg style={styles.eventSubtitle}>
                {new Date(event.start_date).toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric',
                  hour: 'numeric',
                  minute: '2-digit',
                })}
              </TextReg>
            ) : null}
            {!isSummaryStep && (
              <View style={styles.closeButton}>
                <IconButton
                  icon={Cancel01Icon}
                  onPress={handleDismiss}
                  size="small"
                />
              </View>
            )}
          </View>
        </View>
      }
    >
      {!isSummaryStep && (
        <View style={styles.progressBarContainer}>
          <ProgressBar
            currentStep={
              currentStep === 'ticketSelection'
                ? 1
                : currentStep === 'attendeeDetails'
                  ? isTwoSteps
                    ? 1
                    : 2
                  : currentStep === 'payment'
                    ? isTwoSteps
                      ? 2
                      : 3
                    : 0
            }
            totalSteps={isTwoSteps ? 2 : 3}
          />
        </View>
      )}
      {currentStep === 'ticketSelection' && (
        <TicketSelectionStep setIsTwoSteps={setIsTwoSteps} />
      )}

      {currentStep === 'attendeeDetails' && <AttendeeDetailsStep />}

      {currentStep === 'payment' && <PaymentStep />}

      {isSummaryStep && <SummaryStep />}
    </Modal>
  );
}

const createStyles = ({
  isMobile,
  insets,
}: WithResponsive<WithSafeAreaInsetsProps>) =>
  StyleSheet.create({
    modal: {},
    modalContent: {
      padding: 0,
    },
    headerContainer: {
      position: 'relative',
      paddingHorizontal: isMobile ? 12 : 16,
      paddingTop: (isMobile ? 12 : 16) + (insets?.top ?? 0),
      paddingBottom: 16,
      width: '100%',
    },
    closeButton: {
      position: 'absolute',
      justifyContent: 'center',
      width: '100%',
      alignItems: 'flex-end',
      height: '100%',
    },
    header: {
      alignItems: 'center',
      position: 'relative',
    },
    title: {
      fontSize: isMobile ? 18 : 22,
      color: Colors.text,
      textAlign: 'center',
      paddingHorizontal: 32,
    },
    eventSubtitle: {
      fontSize: isMobile ? 12 : 14,
      color: Colors.placeholderText,
      textAlign: 'center',
    },
    progressBarContainer: {
      width: '100%',
      paddingHorizontal: isMobile ? 12 : 16,
      paddingBottom: 8,
      paddingTop: 4,
    },
    titleSkeleton: {
      height: isMobile ? 17.5 : 22,
      marginVertical: 5,
      maxWidth: 200,
      width: '100%',
      borderRadius: 12,
      backgroundColor: Colors.opacity10,
    },
    subtitleSkeleton: {
      height: isMobile ? 12.5 : 16,
      marginVertical: 2.5,
      maxWidth: 120,
      width: '100%',
      borderRadius: 8,
      backgroundColor: Colors.opacity10,
    },
  });
